/**
 * 사업구조화관리 PoC 저장소 (tenant별 localStorage)
 * 검증·예산구분 옵션은 예산과목구조와 공유, 저장 키·프리셋·하류 영향만 사업용.
 */
import {
  YN_OPTIONS,
  RULE_OPTIONS,
  LEVEL_CODE_OPTIONS,
  INOUT_OPTIONS,
  BUDGET_TYPE_OPTIONS,
  BUDGET_TYPE_DEFAULT,
  BUDGET_TYPE_HELP,
  STRUCTURE_BUDGET_TYPE,
  BUDGET_TYPE_CAPITAL,
  isBudgetTypeAll,
  validateStructure,
  buildBudgetLevelColumnFields,
  normalizeStructureGridRows,
  getUsedLevels,
  createStructureUid,
} from 'budget/budgetStructureStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList } from 'services/commonQuery';

export {
  YN_OPTIONS,
  RULE_OPTIONS,
  LEVEL_CODE_OPTIONS,
  INOUT_OPTIONS,
  BUDGET_TYPE_OPTIONS,
  BUDGET_TYPE_DEFAULT,
  BUDGET_TYPE_HELP,
  STRUCTURE_BUDGET_TYPE,
  isBudgetTypeAll,
  validateStructure,
  buildBudgetLevelColumnFields,
  normalizeStructureGridRows,
};

export const PROJECT_STRUCTURE_STORAGE_PREFIX = 'budgetProjectStructure';

const PROJECT_STRUCTURE_SAVE_QUERY = [
  'BdProject.insertProjectStructure',
  'BdProject.updateProjectStructure',
  'BdProject.deleteProjectStructure',
];

const projectStructureCache = new Map();

export const PROJECT_STRUCTURE_HELP =
  '사용(Y)한 레벨이 사업 계층입니다. 최종레벨은 반드시 사용 중인 가장 깊은 단계여야 하며, 예산편성~지출결의까지 그 최종 레벨 사업을 사용합니다. 예: LV1·LV2만 사용·단위 최종 → 편성~지출은 단위사업까지 / LV1~LV4 사용·LV4 최종 → LV4 사업까지. (분야·부문은 사업등록 화면)';

/** 사업구조 확정 시 영향 범위 — 모두 최종레벨(적용 사업 깊이)을 공유 */
export const PROJECT_DOWNSTREAM_IMPACT_SCREENS = [
  { id: 'project_reg', label: '사업등록' },
  { id: 'budget_plan', label: '예산편성' },
  { id: 'budget_alloc', label: '예산배정' },
  { id: 'purchase_req', label: '품의' },
  { id: 'obligation', label: '원인행위' },
  { id: 'expense_resol', label: '지출결의' },
];

/**
 * 프리셋: LV1~LV3 사용(세부=최종). LV4는 미사용 — 필요 시 사용(Y)·최종으로 바꿔 깊이 확장.
 * LV2까지만 쓰려면 LV3 사용=N 후 단위에 최종=Y.
 */
const PROJECT_LEVEL_ROWS = [
  { levelNo: 1, useYn: 'Y', levelCode: 'LV1', levelName: '정책', finalYn: 'N', codeRule: '행정안전부' },
  { levelNo: 2, useYn: 'Y', levelCode: 'LV2', levelName: '단위', finalYn: 'N', codeRule: '행정안전부' },
  { levelNo: 3, useYn: 'Y', levelCode: 'LV3', levelName: '세부', finalYn: 'Y', codeRule: '행정안전부' },
  { levelNo: 4, useYn: 'N', levelCode: 'LV4', levelName: '', finalYn: 'N', codeRule: '자체' },
];

/**
 * 사업구조 검증 + 편성~지출 적용 깊이
 * - 최종레벨 = 사용 중인 가장 깊은 레벨이어야 함
 * - applyLevel* : 사업등록·편성·배정·품의·원인행위·지출결의에서 쓰는 사업 레벨
 */
export function validateProjectStructure(rows = []) {
  const base = validateStructure(rows);
  const deepest = base.used.length ? base.used[base.used.length - 1] : null;
  const apply = base.used.find((r) => r.finalYn === 'Y') || null;
  const finalIsDeepest =
    !!apply && !!deepest && Number(apply.levelNo) === Number(deepest.levelNo);
  const applyLevel = finalIsDeepest ? apply : deepest;
  const applyLevelNo = applyLevel ? Number(applyLevel.levelNo) : 0;
  const applyLevelCode = applyLevel?.levelCode || '';
  const applyLevelName = applyLevel?.levelName || '';

  let applyHint = '사용 레벨과 최종레벨(가장 깊은 단계)을 지정하세요.';
  if (applyLevelName && applyLevelCode) {
    applyHint = `편성~지출은 「${applyLevelName}」(${applyLevelCode}) 사업까지 사용합니다.`;
  }

  return {
    ...base,
    finalIsDeepest,
    canConfirm: base.canConfirm && finalIsDeepest,
    applyLevel,
    applyLevelNo,
    applyLevelCode,
    applyLevelName,
    applyHint,
  };
}

export const PROJECT_STRUCTURE_PRESETS = {
  expense: PROJECT_LEVEL_ROWS,
  income: PROJECT_LEVEL_ROWS.map((r) => ({ ...r })),
};

/** 사업구조 행 정규화 — 삭제(D)·mod 제거 + 분개매핑 필드 제외 */
export function sanitizeProjectStructureRows(rows = []) {
  return normalizeStructureGridRows(rows).map((r) => {
    const { acctMapYn, ...rest } = r || {};
    return rest;
  });
}

export function cloneProjectPreset(inoutDiv = '지출') {
  const key = inoutDiv === '수입' ? 'income' : 'expense';
  return sanitizeProjectStructureRows(JSON.parse(JSON.stringify(PROJECT_STRUCTURE_PRESETS[key])));
}

/** 사업구조 키: 기관·회계연도·목적만 (수지구분·예산구분·적용기간 비사용) */
export function projectStructureStorageKey({ tenantCode, fiscalYear, purpose }) {
  return [
    PROJECT_STRUCTURE_STORAGE_PREFIX,
    tenantCode || '',
    fiscalYear || '',
    purpose || '집행',
  ].join('|');
}

/** PoC 이전 키(수지구분|목적|예산구분) 호환 조회 */
function legacyProjectStructureKeys({ tenantCode, fiscalYear, purpose, inoutDiv, budgetType }) {
  const purposeVal = purpose || '집행';
  const inouts = inoutDiv ? [inoutDiv] : ['지출', '수입'];
  const types = budgetType && !isBudgetTypeAll(budgetType)
    ? [budgetType]
    : [STRUCTURE_BUDGET_TYPE, BUDGET_TYPE_CAPITAL];
  const keys = [];
  inouts.forEach((io) => {
    types.forEach((bt) => {
      keys.push(
        [PROJECT_STRUCTURE_STORAGE_PREFIX, tenantCode || '', fiscalYear || '', io, purposeVal, bt].join('|')
      );
    });
  });
  return keys;
}

function readProjectStructureFromStorage(dims) {
  try {
    const primary = projectStructureStorageKey(dims);
    let raw = localStorage.getItem(primary);
    if (!raw) {
      for (const key of legacyProjectStructureKeys(dims || {})) {
        raw = localStorage.getItem(key);
        if (raw) break;
      }
    }
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** API 행 → 화면 payload (levels_json ↔ rows/usedLevels, confirm_yn ↔ status) */
function mapApiRowToProjectStructure(row, dims) {
  let rows = [];
  try {
    const parsed = row?.levelsJson ? JSON.parse(row.levelsJson) : null;
    rows = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.rows) ? parsed.rows : [];
  } catch {
    rows = [];
  }
  const v = validateProjectStructure(rows);
  return {
    structureUid: row?.structureUid || '',
    dims: { ...dims },
    rows,
    usedLevels: getUsedLevels(rows),
    status: row?.confirmYn === 'Y' ? '확정' : '작성중',
    versionNo: Number(row?.versionNo) || 1,
    confirmedAt: row?.confirmedAt || null,
    confirmedBy: row?.confirmedBy || null,
    applyLevelName: row?.applyLevelName || v.applyLevelName || '',
    applyLevelNo: v.applyLevelNo,
    applyLevelCode: v.applyLevelCode,
    finalLevelName: v.applyLevelName || '',
  };
}

/** 동기 getter용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedProjectStructurePayload(dims) {
  const key = projectStructureStorageKey(dims);
  if (projectStructureCache.has(key)) return projectStructureCache.get(key);
  if (isBdApiEnabled()) return null;
  const payload = readProjectStructureFromStorage(dims);
  projectStructureCache.set(key, payload);
  return payload;
}

export async function loadProjectStructurePayload(dims) {
  const key = projectStructureStorageKey(dims);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdProject.selectProjectStructureList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    const payload = rows.length ? mapApiRowToProjectStructure(rows[0], dims) : null;
    projectStructureCache.set(key, payload);
    return payload;
  }
  const payload = readProjectStructureFromStorage(dims);
  projectStructureCache.set(key, payload);
  return payload;
}

export async function saveProjectStructurePayload(dims, payload) {
  const key = projectStructureStorageKey(dims);
  const cleaned = {
    ...payload,
    rows: sanitizeProjectStructureRows(payload?.rows),
    usedLevels: sanitizeProjectStructureRows(payload?.usedLevels),
  };
  delete cleaned.acctMapLevelNames;
  projectStructureCache.set(key, cleaned);

  if (isBdApiEnabled()) {
    const existing = await commonSelect('BdProject.selectProjectStructureList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    const structureUid =
      existing[0]?.structureUid || cleaned.structureUid || createStructureUid('PS');
    await commonSaveList(
      PROJECT_STRUCTURE_SAVE_QUERY,
      [
        {
          structureUid,
          budgetType: cleaned.budgetType || STRUCTURE_BUDGET_TYPE,
          levelsJson: JSON.stringify(cleaned.rows || []),
          applyLevelName: cleaned.applyLevelName || cleaned.finalLevelName || null,
          confirmYn: cleaned.status === '확정' ? 'Y' : 'N',
          confirmedBy: cleaned.confirmedBy || null,
          confirmedAt: cleaned.confirmedAt
            ? String(cleaned.confirmedAt).replace('T', ' ').replace(/Z$/, '')
            : null,
          versionNo: Number(cleaned.versionNo) || 1,
          version: existing[0]?.version,
          mod: existing.length ? 'U' : 'A',
        },
      ],
      { tenantCode: dims?.tenantCode, fiscalYear: dims?.fiscalYear }
    );
    projectStructureCache.set(key, { ...cleaned, structureUid });
    return key;
  }

  localStorage.setItem(key, JSON.stringify(cleaned));
  localStorage.setItem(`${PROJECT_STRUCTURE_STORAGE_PREFIX}:lastKey`, key);
  return key;
}

/** 사업등록 화면 연동 전 — 확정해제 항상 허용 */
export function canUnconfirmProjectStructure() {
  return { ok: true, subjectCount: 0 };
}

export function getConfirmedProjectStructure(dims) {
  const data = getCachedProjectStructurePayload(dims);
  if (!data || data.status !== '확정') return null;
  const used = data.usedLevels?.length
    ? data.usedLevels
    : (data.rows || []).filter((r) => r.useYn === 'Y' && String(r.levelName || '').trim());
  if (!used.length) return null;
  const v = validateProjectStructure(data.rows || used);
  return {
    dims,
    status: data.status,
    usedLevels: used,
    finalLevelName: data.finalLevelName || v.applyLevelName || '',
    applyLevelNo: data.applyLevelNo || v.applyLevelNo,
    applyLevelCode: data.applyLevelCode || v.applyLevelCode,
    applyLevelName: data.applyLevelName || v.applyLevelName,
    rows: sanitizeProjectStructureRows(data.rows || []),
    versionNo: data.versionNo || 1,
    confirmedAt: data.confirmedAt || null,
    confirmedBy: data.confirmedBy || null,
  };
}

export async function copyProjectStructureToNextYear(dims, { userId } = {}) {
  const data = await loadProjectStructurePayload(dims);
  if (!data?.rows?.length) return { ok: false, reason: '복사할 사업구조가 없습니다.' };
  const nextYear = String(Number(dims.fiscalYear) + 1);
  if (!Number.isFinite(Number(dims.fiscalYear))) {
    return { ok: false, reason: '회계년도가 올바르지 않습니다.' };
  }
  const nextDims = { ...dims, fiscalYear: nextYear };
  const existing = await loadProjectStructurePayload(nextDims);
  if (existing?.status === '확정') {
    return { ok: false, reason: `${nextYear}년 사업구조가 이미 확정되어 있습니다.` };
  }
  const payload = {
    ...data,
    dims: nextDims,
    status: '작성중',
    versionNo: (Number(data.versionNo) || 1) + 1,
    startDate: `${nextYear}-01-01`,
    endDate: `${nextYear}-12-31`,
    confirmedAt: null,
    confirmedBy: null,
    unconfirmHistory: data.unconfirmHistory || [],
    copiedFrom: { ...dims, versionNo: data.versionNo || 1 },
    savedAt: new Date().toISOString(),
    savedBy: userId || '',
  };
  await saveProjectStructurePayload(nextDims, payload);
  return { ok: true, nextDims, payload };
}
